// Structured, low-noise diagnostic logger for the WebRTC meeting pipeline.
//
// Purpose: make the tile-rendering + transport/producer/consumer lifecycle
// observable in the browser console so we can see *exactly* where a peer's video
// stops flowing (no consumer? consumer but muted track? transport never
// connected? cross-node newConsumer never arrived?).
//
// Enabled when `VITE_VC_DEBUG=1` OR in any dev build. Every line is tagged
// `[vc:<area>]` with a monotonic-ish wall-clock timestamp and a compact payload,
// so you can filter the console by `vc:` and grep by area (tile/transport/
// producer/consumer/socket/presence).

const env = (import.meta as unknown as { env: Record<string, unknown> }).env

// VITE_VC_DEBUG is a string ("1"); import.meta.env.DEV is a real boolean in dev.
//
// The localStorage switch matters: both build-time flags are off in the
// deployed bundle, so diagnosing a live meeting used to require a rebuild and
// redeploy. Now `localStorage.setItem('vc:debug','1')` + reload turns it on for
// one browser, which is how you get the peer's side of a media problem.
function debugRequested(): boolean {
  try {
    return localStorage.getItem('vc:debug') === '1'
  } catch {
    return false // private mode / storage disabled
  }
}

const ENABLED = env.VITE_VC_DEBUG === '1' || env.DEV === true || debugRequested()

type Area = 'join' | 'transport' | 'producer' | 'consumer' | 'tile' | 'socket' | 'presence' | 'stage' | 'screen'

function ts(): string {
  // HH:MM:SS.mmm — enough to line up with the SFU logs during a local repro.
  const d = new Date()
  const p = (n: number, w = 2) => String(n).padStart(w, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}.${p(d.getMilliseconds(), 3)}`
}

/** Log a pipeline event. No-op unless debug is enabled, so it's free in prod. */
export function vclog(area: Area, event: string, data?: Record<string, unknown>): void {
  if (!ENABLED) return

  console.log(`${ts()} [vc:${area}] ${event}`, data ?? '')

  // tools/meeting_driver.py's PC_SHIM pre-creates window.__vclogs so its
  // `console` command can read logs back over CDP without scraping the
  // console — but nothing was ever pushing into it, so that command always
  // returned an empty array. This is the other half.
  try {
    const bucket = (window as unknown as { __vclogs?: unknown[] }).__vclogs
    if (Array.isArray(bucket)) bucket.push({ t: ts(), area, event, data })
  } catch {
    // non-browser eval context — nothing to push into
  }
}

export const vcDebugEnabled = ENABLED
